import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useReads } from "@/contexts/ReadsContext";
import { Button } from "@/components/ui/button";
import { Bookmark, BookmarkCheck, Route as RouteIcon, Check } from "lucide-react";
import { PATHWAY } from "@/constants/testIds";
import { toast } from "sonner";
import { PathwayProgress, ReadBadge } from "@/components/ReadIndicators";
import PageSections from "@/components/PageSections";
import { defaultPathwayIntroSections } from "@/components/sectionTypes";

export function PathwaysList() {
  const { user } = useAuth();
  const { isRead } = useReads();
  const [pathways, setPathways] = useState([]);
  useEffect(() => { api.get("/pathways").then(r => setPathways(r.data)); }, []);
  return (
    <div className="max-w-6xl mx-auto px-6 md:px-10 py-16">
      <div className="text-xs uppercase tracking-[0.24em] text-sky-700 mb-3">Curated reading</div>
      <h1 className="font-heading text-4xl md:text-5xl font-semibold text-slate-900 dark:text-slate-100">Pathways</h1>
      <p className="mt-4 text-slate-600 dark:text-slate-400 max-w-2xl">
        Pathways are ordered sequences through the archive — a way to make sense of a hard subject one step at a time.
      </p>
      <div className="mt-12 grid md:grid-cols-2 gap-6">
        {pathways.length === 0 && (
          <div className="col-span-full border border-dashed border-slate-300 rounded-lg p-12 text-center text-slate-500">
            No pathways yet.
          </div>
        )}
        {pathways.map(p => {
          // `resource_count` is authoritative — reflects blog_ids that
          // resolve to a live blog, not raw pathway.blog_ids length.
          const total = p.resource_count ?? p.blog_ids?.length ?? 0;
          const read = user ? (p.blog_ids || []).filter(id => isRead(id)).length : 0;
          return (
            <Link key={p.pathway_id} to={`/pathways/${p.slug}`}
                  className="border border-slate-200 dark:border-slate-700 rounded-lg p-6 hover:border-sky-700 transition-colors flex flex-col">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="text-xs uppercase tracking-[0.2em] text-sky-700 mb-2">
                    {total} resource{total === 1 ? "" : "s"}
                  </div>
                  <div className="font-heading text-xl font-semibold text-slate-900 dark:text-slate-100">{p.title}</div>
                  <p className="mt-2 text-sm text-slate-600 dark:text-slate-400 leading-relaxed">{p.description}</p>
                </div>
                <RouteIcon className="w-5 h-5 text-slate-400 shrink-0"/>
              </div>
              {user && total > 0 && (
                <PathwayProgress read={read} total={total} size="sm" className="mt-5"/>
              )}
            </Link>
          );
        })}
      </div>
    </div>
  );
}

export function PathwayView() {
  const { slug } = useParams();
  const { user } = useAuth();
  const { isRead } = useReads();
  const [pathway, setPathway] = useState(null);
  const [bookmarked, setBookmarked] = useState(false);

  useEffect(() => { api.get(`/pathways/${slug}`).then(r => setPathway(r.data)); }, [slug]);
  useEffect(() => {
    if (!user || !pathway) return;
    api.get("/bookmarks").then(r => {
      setBookmarked(r.data.raw.some(x => x.target_type === "pathway" && x.target_id === pathway.pathway_id));
    }).catch(() => {});
  }, [user, pathway]);

  const toggle = async () => {
    if (!user) return toast("Sign in to bookmark");
    const r = await api.post("/bookmarks/toggle", { target_type: "pathway", target_id: pathway.pathway_id });
    setBookmarked(r.data.bookmarked);
    toast(r.data.bookmarked ? "Bookmarked" : "Removed bookmark");
  };

  if (!pathway) return <div className="max-w-3xl mx-auto p-12 text-slate-500">Loading…</div>;

  const blogs = pathway.blogs || [];
  const total = blogs.length;
  const readCount = user ? blogs.filter(b => isRead(b.blog_id)).length : 0;

  // Find next unread blog for "Resume where you left off"
  const nextUp = user && total > 0 ? blogs.find(b => !isRead(b.blog_id)) : null;

  return (
    <div data-testid={PATHWAY.view} className="max-w-4xl mx-auto px-6 md:px-10 py-16">
      <PageSections sections={pathway.intro_sections} defaults={defaultPathwayIntroSections(pathway)}/>

      {user && total > 0 && (
        <div className="mt-8 p-5 border border-slate-200 dark:border-slate-700 rounded-lg bg-slate-50 dark:bg-slate-900">
          <PathwayProgress read={readCount} total={total}/>
          {nextUp && (
            <Link to={`/blogs/${nextUp.slug}`} data-testid="pathway-resume-link"
                  className="mt-4 inline-block text-sm text-sky-700 hover:text-sky-900 font-medium">
              {readCount === 0 ? "Start reading" : "Continue"} → {nextUp.title}
            </Link>
          )}
          {!nextUp && readCount === total && (
            <div data-testid="pathway-complete-msg" className="mt-4 text-sm text-emerald-700 dark:text-emerald-400 font-medium">
              You&apos;ve completed this pathway 🎉
            </div>
          )}
        </div>
      )}

      <div className="mt-8">
        <Button data-testid={PATHWAY.bookmark} onClick={toggle} variant="outline" className="border-slate-300">
          {bookmarked ? <><BookmarkCheck className="w-4 h-4 mr-2 text-sky-700"/>Bookmarked</> : <><Bookmark className="w-4 h-4 mr-2"/>Bookmark pathway</>}
        </Button>
      </div>

      <ol className="mt-12 relative border-l-2 border-slate-200 dark:border-slate-700">
        {blogs.map((b, i) => {
          const done = user && isRead(b.blog_id);
          return (
            <li key={b.blog_id} className="pl-8 pb-10 relative">
              <span className={`absolute -left-[13px] top-0 w-6 h-6 rounded-full grid place-items-center text-[11px] font-bold transition-colors ${
                done
                  ? "bg-emerald-600 border-2 border-emerald-600 text-white"
                  : "bg-white dark:bg-slate-900 border-2 border-sky-700 text-sky-700"
              }`}>
                {done ? <Check className="w-3.5 h-3.5"/> : (i + 1)}
              </span>
              <Link to={`/blogs/${b.slug}`} data-testid={PATHWAY.blogLink(b.blog_id)}
                    className="group inline-block">
                <div className="flex items-center gap-3">
                  <div className={`font-heading text-xl font-semibold group-hover:text-sky-700 ${done ? "text-slate-500 dark:text-slate-400" : "text-slate-900 dark:text-slate-100"}`}>
                    {b.title}
                  </div>
                  {done && <ReadBadge read/>}
                </div>
                {b.excerpt && <p className="mt-1 text-sm text-slate-600 dark:text-slate-400 max-w-2xl">{b.excerpt}</p>}
              </Link>
            </li>
          );
        })}
        {blogs.length === 0 && (
          <li className="pl-8 text-slate-500">No blogs in this pathway yet.</li>
        )}
      </ol>
    </div>
  );
}
